/**
 * App Registry — the declarative catalog of consumer-simple Apps.
 *
 * An App is a job-shaped, C-Mode-only product surface on the foundation:
 * one job statement, one route family, one module+flag pair (build contract
 * §2.6), one entitlement key. Apps read and write only canonical objects
 * through existing stores/RPCs — a view, never a schema (Doc 26 rule 7).
 *
 * Routing consumes this registry to mount route families; AppsHome consumes
 * it as the launcher catalog; RequireAppAccess consumes it to gate.
 * Steering spec: PRD-app-layer §5.2 (FR-10).
 */

import type { EntitlementKey } from '@/lib/entitlements';
import type { WorkspaceType } from '@/lib/workspaceModulesService';

/** Reusable App UI shapes (PRD §5.3). Apps beyond the first wave are
 *  predominantly authored (Blueprint + registry entry), not engineered. */
export type SurfaceArchetype =
  | 'rank-a-pool'
  | 'continuous-focus'
  | 'intake-funnel'
  | 'cadenced-review'
  | 'loop'
  | 'brief'
  | 'plan-pipeline'
  | 'front-door'
  | 'record-log'
  /** Objectives on one axis, the work behind them on the other. First consumer: Align. */
  | 'alignment-map'
  /** A finite envelope distributed across competing asks. First consumer: Allocate. */
  | 'allocation'
  /** A gathering that must end in decisions and owners. First consumer: Offsite. */
  | 'event-room';

/** Semantic accent per app — same meaning as the platform color system:
 *  mustard = priority/ranking, slate = structure/organization,
 *  green = execution/health, coral = action/urgency. */
export type AppAccent = 'slate' | 'mustard' | 'green' | 'coral';

export interface AppDefinition {
  /** Stable id, also the route segment under /apps/. */
  appId: string;
  /** Consumer-facing name. */
  name: string;
  /** The want-case root: "[Persona] wants to [verb phrase]" (Doc 26 §2). */
  job: string;
  /** One-line consumer description shown on the launcher card. */
  tagline: string;
  /** The app's single route family. Never a second ungated URL (§2.6). */
  routeFamily: string;
  /** Registered workspace module key (pairs with a feature flag of the same key). */
  moduleKey: string;
  /** Commercial gate, resolved by plan (entitlement = purchased, flag = rolled out). */
  entitlementKey: EntitlementKey;
  archetype: SurfaceArchetype;
  accent: AppAccent;
  /** 'retired' = kept on record, not offered: its route family opens `replacedBy` (DEPRECATION_REGISTER). */
  status: 'live' | 'retired';
  /** For a retired app: the appId that took over its job. */
  replacedBy?: string;
  /**
   * The kinds of workspace the app is offered in — Individual (`personal`),
   * Team (`team`), Organization (`organizational`), Doc 03 § Product Tiers.
   * Every app says, none is assumed: the launcher, sidebar and home widget
   * show an app only in the kinds it names. The individual apps (Mandate,
   * First 100 Days, What's Next) are `['personal']` and sit under "For you"
   * (ruling R18, roadmap S-16). Where an app is offered, not who may open it:
   * the route stays behind the module, flag and entitlement like every app.
   */
  tiers: readonly WorkspaceType[];
  /**
   * In My apps (/apps) until the person takes it off: the small starter set.
   * Every other app waits in the catalog (/apps/catalog) until they add it.
   * A person's own choice always wins (get_my_apps / set_my_app, migration 1064).
   */
  inMyAppsByDefault?: boolean;
}

/** Offered in every kind of workspace: one person's work and a group's alike. */
const EVERY_TIER: readonly WorkspaceType[] = ['personal', 'team', 'organizational'];
/** Offered where there is a group: the job needs more than one person. */
const GROUP_TIERS: readonly WorkspaceType[] = ['team', 'organizational'];
/** Offered only in a personal workspace (ruling R18). */
const INDIVIDUAL: readonly WorkspaceType[] = ['personal'];

export const APP_REGISTRY: AppDefinition[] = [
  {
    appId: 'start',
    name: 'Start',
    job: 'Anyone wants to name what they are trying to do and land in the right flow with everything reducible already decided.',
    tagline: 'Say what you\'re trying to do — start in the right place.',
    routeFamily: '/apps/start',
    moduleKey: 'app_start',
    entitlementKey: 'app_start',
    archetype: 'front-door',
    accent: 'coral',
    status: 'live',
    tiers: EVERY_TIER,
  },
  {
    appId: 'session',
    name: 'Session',
    job: 'A group wants to weigh in on a set of options together and leave with a defensible result.',
    tagline: 'Run a prioritization session — set it up, invite people, decide together.',
    routeFamily: '/apps/session',
    moduleKey: 'app_session',
    entitlementKey: 'app_session',
    archetype: 'rank-a-pool',
    accent: 'mustard',
    status: 'live',
    tiers: EVERY_TIER,
    inMyAppsByDefault: true,
  },
  {
    appId: 'rank',
    name: 'Rank',
    job: 'A facilitator wants a group ranking of a set of options — S5 only, without designing the rest of an event.',
    tagline: 'Rank a pool of options together and leave with an ordered result.',
    routeFamily: '/apps/rank',
    moduleKey: 'app_rank',
    entitlementKey: 'app_rank',
    archetype: 'rank-a-pool',
    accent: 'mustard',
    // Retired 2026-10-05 (DEP-029): it was the Session app under a second name —
    // the same setup, host and join screens, and the same list of sessions.
    status: 'retired',
    tiers: EVERY_TIER,
    replacedBy: 'session',
  },
  {
    appId: 'cut',
    name: 'Cut',
    job: 'A facilitator wants to draw a cut line through a pool of candidates and keep the whole list as a pool, with what is above the line as the working set.',
    tagline: 'Order candidates, draw the cut, and save the list as an item pool with the cut line in place.',
    routeFamily: '/apps/cut',
    moduleKey: 'app_cut',
    entitlementKey: 'app_cut',
    archetype: 'rank-a-pool',
    accent: 'slate',
    status: 'live',
    tiers: EVERY_TIER,
  },
  {
    appId: 'items',
    name: 'Items',
    job: 'A team member wants one place to capture and manage the things that might matter.',
    tagline: 'Capture, organize, and keep track of the items every decision draws from.',
    routeFamily: '/apps/items',
    moduleKey: 'app_items',
    entitlementKey: 'app_items',
    archetype: 'intake-funnel',
    accent: 'slate',
    status: 'live',
    tiers: EVERY_TIER,
    inMyAppsByDefault: true,
  },
  {
    appId: 'session-import',
    name: 'Record',
    job: 'A facilitator wants to bring a prioritization that already happened onto the record.',
    tagline: 'Ran the prioritization somewhere else? Import the results — items, ranking, scores.',
    routeFamily: '/apps/session-import',
    moduleKey: 'app_session_import',
    entitlementKey: 'app_session_import',
    archetype: 'intake-funnel',
    accent: 'green',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'decisions',
    name: 'Decisions',
    job: 'A leader wants the org’s consequential decisions on the record — and any one of them shareable as a URL.',
    tagline: 'Browse every memorialized decision, record one, and answer “why?” with a link.',
    routeFamily: '/apps/decisions',
    moduleKey: 'app_decisions',
    entitlementKey: 'app_decisions',
    archetype: 'record-log',
    accent: 'slate',
    status: 'live',
    tiers: EVERY_TIER,
    inMyAppsByDefault: true,
  },
  {
    appId: 'viability',
    name: 'Viability',
    job: 'An owner wants to know whether the plan is deliverable — and, when it is, when it can launch.',
    tagline: 'Check whether your priorities fit your capacity, then plan the launch calendar.',
    routeFamily: '/apps/viability',
    moduleKey: 'app_viability',
    entitlementKey: 'app_viability',
    archetype: 'plan-pipeline',
    accent: 'green',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'focus',
    name: 'Focus',
    job: 'A team lead wants the team pointed at the few things that matter this period — visibly, durably.',
    tagline: 'The few things you are committed to right now, and the pool behind them.',
    routeFamily: '/apps/focus',
    moduleKey: 'app_focus',
    entitlementKey: 'app_focus',
    archetype: 'continuous-focus',
    accent: 'mustard',
    status: 'live',
    tiers: GROUP_TIERS,
    inMyAppsByDefault: true,
  },
  {
    appId: 'intake',
    name: 'Intake',
    job: 'A team wants one mouth for ideas instead of five channels — and submitters want to see what happened.',
    tagline: 'One place to send an idea, and an honest answer about where it went.',
    routeFamily: '/apps/intake',
    moduleKey: 'app_intake',
    entitlementKey: 'app_intake',
    archetype: 'intake-funnel',
    accent: 'slate',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'review',
    name: 'Review',
    job: 'An owner wants the periodic re-look: what moved, what changed, is the ranking still true?',
    tagline: 'What entered, what left, what moved since you last published.',
    routeFamily: '/apps/review',
    moduleKey: 'app_review',
    entitlementKey: 'app_review',
    archetype: 'cadenced-review',
    accent: 'coral',
    status: 'live',
    tiers: EVERY_TIER,
  },
  {
    appId: 'classification',
    name: 'Classification',
    job: 'An owner wants category and altitude trustworthy — clear the proposal queue and confirm classification.',
    tagline: 'Review AI classification proposals and confirm category and altitude.',
    routeFamily: '/apps/classification',
    moduleKey: 'app_classification',
    entitlementKey: 'app_classification',
    archetype: 'cadenced-review',
    accent: 'mustard',
    status: 'live',
    tiers: EVERY_TIER,
  },
  {
    appId: 'relationships',
    name: 'Relationships',
    job: 'A PM wants to see AI-suggested connections and confirm how items link before ranking.',
    tagline: 'Review AI relationship suggestions and see how items connect.',
    routeFamily: '/apps/relationships',
    moduleKey: 'app_relationships',
    entitlementKey: 'app_relationships',
    archetype: 'cadenced-review',
    accent: 'slate',
    status: 'live',
    tiers: EVERY_TIER,
  },
  {
    appId: 'allocate',
    name: 'Allocate',
    job: 'A budget owner wants to split a finite resource across competing asks — and show their work.',
    tagline: 'Split the envelope, see who wanted what, and leave with a defensible number.',
    routeFamily: '/apps/allocate',
    moduleKey: 'app_allocate',
    entitlementKey: 'app_allocate',
    archetype: 'allocation',
    accent: 'mustard',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'offsite',
    name: 'Offsite',
    job: 'An organizer wants the offsite to end with decisions, not vibes.',
    tagline: 'Collect the topics, run the room, leave with who owns what.',
    routeFamily: '/apps/offsite',
    moduleKey: 'app_offsite',
    entitlementKey: 'app_offsite',
    archetype: 'event-room',
    accent: 'coral',
    status: 'live',
    tiers: GROUP_TIERS,
  },

  // ── Authored tail ──────────────────────────────────────────────────────────
  // The apps below share one renderer (`blueprints/BlueprintApp`) and differ
  // only by their record in `blueprints/catalog.ts`. They are separate products
  // commercially — own route family, module, flag and entitlement each (§2.6) —
  // but no longer separate code. Adding the next one is authoring, not building.
  {
    appId: 'hire',
    name: 'Hire',
    job: 'A hiring manager wants a panel decision that reflects everyone’s judgement, not whoever spoke first.',
    tagline: 'Score candidates against agreed criteria — independently, then decide.',
    routeFamily: '/apps/hire',
    moduleKey: 'app_hire',
    entitlementKey: 'app_hire',
    archetype: 'rank-a-pool',
    accent: 'slate',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'vendor-select',
    name: 'Vendor Select',
    job: 'A buyer wants to choose a vendor and be able to defend the choice later.',
    tagline: 'Compare vendors on what actually matters — and keep the receipts.',
    routeFamily: '/apps/vendor-select',
    moduleKey: 'app_vendor_select',
    entitlementKey: 'app_vendor_select',
    archetype: 'rank-a-pool',
    accent: 'slate',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'risk-triage',
    name: 'Risk Triage',
    job: 'An owner wants to know which risks to work this week and which to just watch.',
    tagline: 'Find the risks worth acting on, and give each one an owner.',
    routeFamily: '/apps/risk-triage',
    moduleKey: 'app_risk_triage',
    entitlementKey: 'app_risk_triage',
    archetype: 'rank-a-pool',
    accent: 'coral',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'post-mortem',
    name: 'Post-Mortem',
    job: 'A team wants the review to end in owned fixes rather than a document nobody reopens.',
    tagline: 'Agree what mattered, then leave with owners on the fixes.',
    routeFamily: '/apps/post-mortem',
    moduleKey: 'app_post_mortem',
    entitlementKey: 'app_post_mortem',
    archetype: 'rank-a-pool',
    accent: 'green',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'ask-the-room',
    name: 'Ask the Room',
    job: 'Anyone wants a fast, honest read on what a group thinks.',
    tagline: 'Put a question up, get an answer in two minutes.',
    routeFamily: '/apps/ask-the-room',
    moduleKey: 'app_ask_the_room',
    entitlementKey: 'app_ask_the_room',
    archetype: 'rank-a-pool',
    accent: 'mustard',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  {
    appId: 'align',
    name: 'Align',
    job: 'A leadership team wants to see whether what they are doing maps to what they said matters.',
    tagline: 'Every objective, and the work actually standing behind it.',
    routeFamily: '/apps/align',
    moduleKey: 'app_align',
    entitlementKey: 'app_align',
    archetype: 'alignment-map',
    accent: 'green',
    status: 'live',
    tiers: GROUP_TIERS,
  },
  // The individual apps (ruling R18, roadmap S-16): offered in personal workspaces.
  {
    appId: 'mandate',
    name: 'Mandate',
    job: 'A leader new to a role wants to frame what they were brought in to do and keep the record of how they decided it.',
    tagline: 'Your new role: the outcome you owe, your priorities, and the record of how you decided.',
    routeFamily: '/apps/mandate',
    moduleKey: 'app_mandate',
    entitlementKey: 'app_mandate',
    archetype: 'record-log',
    accent: 'slate',
    status: 'live',
    tiers: INDIVIDUAL,
    inMyAppsByDefault: true,
  },
  {
    appId: 'whats-next',
    name: "What's Next",
    job: 'A person wants to start each day clear on the three things that matter and time set aside for the one that moves them forward.',
    tagline: 'Morning Boot: clear your plate, rank your top three, and timebox your North Star.',
    routeFamily: '/apps/whats-next',
    moduleKey: 'app_whats_next',
    entitlementKey: 'app_whats_next',
    archetype: 'continuous-focus',
    accent: 'mustard',
    status: 'live',
    tiers: INDIVIDUAL,
    inMyAppsByDefault: true,
  },
  {
    appId: 'first-100-days',
    name: 'First 100 Days',
    job: 'A leader new to a role wants to spend their first hundred days listening, sorting what they inherit, and come out with their first priorities ranked and recorded.',
    tagline: 'Your first hundred days in a new role, on your mandate: listen, sort, and rank what you will drive.',
    routeFamily: '/apps/first-100-days',
    moduleKey: 'app_first_100_days',
    entitlementKey: 'app_first_100_days',
    archetype: 'plan-pipeline',
    accent: 'green',
    status: 'live',
    tiers: INDIVIDUAL,
  },
];

export function getAppDefinition(appId: string): AppDefinition | undefined {
  return APP_REGISTRY.find((app) => app.appId === appId);
}

/** Tailwind class fragments per accent. Centralized so every app surface
 *  renders identity the same way (suite consistency). */
export const APP_ACCENT_CLASSES: Record<
  AppAccent,
  { chipBg: string; chipText: string; ring: string; bar: string }
> = {
  mustard: {
    chipBg: 'bg-electric-mustard-100 dark:bg-electric-mustard-900/30',
    chipText: 'text-electric-mustard-800 dark:text-electric-mustard-300',
    ring: 'ring-electric-mustard-500/40',
    bar: 'bg-electric-mustard-500',
  },
  slate: {
    chipBg: 'bg-slate-blue-100 dark:bg-slate-blue-900/30',
    chipText: 'text-slate-blue-700 dark:text-slate-blue-300',
    ring: 'ring-slate-blue-500/40',
    bar: 'bg-slate-blue-500',
  },
  green: {
    chipBg: 'bg-alpine-green-100 dark:bg-alpine-green-900/30',
    chipText: 'text-alpine-green-700 dark:text-alpine-green-300',
    ring: 'ring-alpine-green-500/40',
    bar: 'bg-alpine-green-500',
  },
  coral: {
    chipBg: 'bg-luminous-coral-100 dark:bg-luminous-coral-900/30',
    chipText: 'text-luminous-coral-700 dark:text-luminous-coral-300',
    ring: 'ring-luminous-coral-500/40',
    bar: 'bg-luminous-coral-500',
  },
};
